import { createTheme, ThemeOptions } from '@mui/material/styles';
import { lightTheme as appLightTheme } from './light-theme';
import { darkTheme as appDarkTheme } from './dark-theme';

declare module '@mui/material/styles' {
  interface Palette {
    textButton: string;
  }
  interface PaletteOptions {
    textButton?: string;
  }
}

const commonSettings: ThemeOptions = {
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: "'Geist Sans', Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    h1: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
    h2: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
    h3: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
    h4: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
    h5: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
    h6: { fontFamily: 'Poppins, sans-serif', fontWeight: 700 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
          boxShadow: 'none',
          padding: '8px 20px',
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap' as const,
          flexShrink: 0,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(59, 192, 183, 0.25)',
          },
          ...(ownerState.color !== 'inherit' && {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.textButton,
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(59, 192, 183, 0.25)',
            },
            ...(ownerState.color === 'error' && {
              backgroundColor: theme.palette.error.main,
              color: theme.palette.textButton,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.25)',
              },
            }),
            ...(ownerState.color === 'primary' && {
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || '#2a9d95'} 100%)`,
              color: '#fff',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark || '#2a9d95'} 0%, ${theme.palette.primary.main} 100%)`,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(59, 192, 183, 0.35)',
              },
            }),
          }),
          ...(ownerState.variant === 'outlined' && {
            borderWidth: '1.5px',
            // Reset the `background` shorthand too: the primary block above sets a
            // green gradient via `background`, and clearing only `backgroundColor`
            // would let that gradient leak through on outlined buttons.
            background: 'transparent',
            backgroundColor: 'transparent',
            color:
              ownerState.color === 'error'
                ? theme.palette.error.main
                : ownerState.color === 'primary'
                ? theme.palette.primary.main
                : ownerState.color === 'secondary'
                ? theme.palette.secondary.main
                : ownerState.color === 'success'
                ? theme.palette.success.main
                : ownerState.color === 'info'
                ? theme.palette.info.main
                : ownerState.color === 'warning'
                ? theme.palette.warning.main
                : undefined,
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor:
                ownerState.color === 'error'
                  ? 'rgba(231, 76, 60, 0.06)'
                  : 'rgba(59, 192, 183, 0.06)',
              color:
                ownerState.color === 'error'
                  ? theme.palette.error.main
                  : ownerState.color === 'primary'
                  ? theme.palette.primary.main
                  : ownerState.color === 'secondary'
                  ? theme.palette.secondary.main
                  : ownerState.color === 'success'
                  ? theme.palette.success.main
                  : ownerState.color === 'info'
                  ? theme.palette.info.main
                  : ownerState.color === 'warning'
                  ? theme.palette.warning.main
                  : undefined,
              transform: 'translateY(-1px)',
              boxShadow: 'none',
            },
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          backdropFilter: 'blur(20px) saturate(180%)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          padding: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'box-shadow 0.2s ease',
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(59, 192, 183, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        // Solo i chip su cui si clicca davvero: erano alti 24px, ad esempio
        // quello dello stato del volo. 32 e' un compromesso, perche' in una
        // tabella densa portarli a 44 sfalderebbe le righe.
        clickable: {
          minHeight: 32,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.06)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            textTransform: 'uppercase' as const,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(59, 192, 183, 0.04)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.8125rem',
          padding: '8px 14px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 600,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        // L'intestazione ordinabile era alta quanto il testo: 24px.
        root: {
          minHeight: 36,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // I pulsanti-icona piccoli misuravano 30x30: pochi per un dito, e in
        // pista si usano col dito. Un minimo di 36 li rende centrabili senza
        // stringere nulla (e' un minimo, non una dimensione fissa).
        sizeSmall: {
          minWidth: 36,
          minHeight: 36,
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'light',
    primary: {
      main: appLightTheme.primary,
      light: appLightTheme.primaryLight,
      dark: appLightTheme.primaryDark,
    },
    secondary: {
      main: appLightTheme.secondary,
    },
    text: {
      primary: appLightTheme.textPrimary,
    },
    background: {
      default: appLightTheme.background,
      paper: appLightTheme.backgroundPaper,
    },
    divider: appLightTheme.divider,
    success: {
      main: appLightTheme.success,
    },
    error: {
      main: appLightTheme.error,
      dark: appLightTheme.errorDark,
    },
    info: {
      main: appLightTheme.info,
    },
    warning: {
      main: appLightTheme.warning,
    },
    textButton: appLightTheme.textButton,
  },
  components: {
    ...commonSettings.components,
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          background: `${appLightTheme.background}`,
          backgroundImage: `
            radial-gradient(ellipse 80% 60% at 95% 0%, rgba(59,130,183,0.13) 0%, transparent 55%),
            radial-gradient(ellipse 65% 55% at 0% 95%, rgba(59,192,183,0.12) 0%, transparent 50%),
            radial-gradient(ellipse 50% 40% at 10% 20%, rgba(200,140,180,0.07) 0%, transparent 50%)
          `,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        '::selection': {
          backgroundColor: 'rgba(59, 192, 183, 0.2)',
          color: 'inherit',
        },
      },
    },
    MuiOutlinedInput: {
      ...(commonSettings.components?.MuiOutlinedInput || {}),
      styleOverrides: {
        ...((commonSettings.components?.MuiOutlinedInput as any)?.styleOverrides || {}),
        root: {
          borderRadius: 10,
          transition: 'box-shadow 0.2s ease',
          backgroundColor: 'rgba(244, 247, 246, 0.5)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: appLightTheme.inputBorder,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: appLightTheme.primary,
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(59, 192, 183, 0.15)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F0F2F5',
          '& .MuiTableCell-head': {
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: appLightTheme.textMuted,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
          borderBottomColor: appLightTheme.divider,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${appLightTheme.divider}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: appLightTheme.divider,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${appLightTheme.divider}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: `1px solid ${appLightTheme.divider}`,
          backdropFilter: 'blur(20px) saturate(180%)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'dark',
    primary: {
      main: appDarkTheme.primary,
      light: appDarkTheme.primaryLight,
      dark: appDarkTheme.primaryDark,
    },
    secondary: {
      main: appDarkTheme.secondary,
    },
    text: {
      primary: appDarkTheme.textPrimary,
      secondary: appDarkTheme.textSecondary,
    },
    background: {
      default: appDarkTheme.background,
      paper: appDarkTheme.backgroundPaper,
    },
    divider: appDarkTheme.divider,
    success: {
      main: appDarkTheme.success,
    },
    error: {
      main: appDarkTheme.error,
      dark: appDarkTheme.errorDark,
    },
    info: {
      main: appDarkTheme.info,
    },
    warning: {
      main: appDarkTheme.warning,
    },
    textButton: appDarkTheme.textButton,
  },
  components: {
    ...commonSettings.components,
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          background: `${appDarkTheme.background}`,
          backgroundImage: `
            radial-gradient(ellipse 80% 60% at 100% 0%, rgba(59,192,183,0.14) 0%, transparent 55%),
            radial-gradient(ellipse 50% 40% at 0% 100%, rgba(42,157,149,0.10) 0%, transparent 50%),
            radial-gradient(ellipse 40% 30% at 50% 50%, rgba(59,192,183,0.05) 0%, transparent 60%)
          `,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        '::selection': {
          backgroundColor: 'rgba(59, 192, 183, 0.3)',
          color: 'inherit',
        },
        '.rbc-calendar': {
          color: appDarkTheme.textPrimary,
        },
        '.rbc-off-range-bg': {
          backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
        },
        '.rbc-off-range': {
          color: `${appDarkTheme.textMuted} !important`,
        },
        '.rbc-header': {
          color: appDarkTheme.textPrimary,
        },
        '.rbc-day-bg': {
          borderColor: 'rgba(255,255,255,0.1)',
        },
        '.rbc-month-view, .rbc-time-view, .rbc-agenda-view': {
          border: '1px solid rgba(255,255,255,0.1)',
        },
        '.rbc-month-row': {
          border: '1px solid rgba(255,255,255,0.1)',
        },
        '.rbc-header + .rbc-header': {
          borderLeft: '1px solid rgba(255,255,255,0.1)',
        },
        '.rbc-day-bg + .rbc-day-bg': {
          borderLeft: '1px solid rgba(255,255,255,0.1)',
        },
        '.rbc-btn-group button': {
          color: appDarkTheme.textPrimary,
          backgroundColor: appDarkTheme.backgroundPaper,
          borderColor: 'rgba(255,255,255,0.1)',
        },
        // Il CSS di react-big-calendar, su hover e focus, rimette il suo testo
        // scuro (#373a3c) e il suo bordo grigio: sul fondo scuro il pulsante
        // diventava un rettangolo nero illeggibile. Si ridichiarano anche
        // colore e bordo, con la stessa specificita' della regola sua.
        '.rbc-toolbar button:hover, .rbc-toolbar button:focus': {
          color: appDarkTheme.textPrimary,
          backgroundColor: 'rgba(255,255,255,0.12)',
          borderColor: 'rgba(255,255,255,0.25)',
        },
        '.rbc-toolbar button:active, .rbc-toolbar button.rbc-active': {
          backgroundColor: `${appDarkTheme.primary} !important`,
          color: '#fff !important',
        },
        '.rbc-today': {
          backgroundColor: 'rgba(59, 192, 183, 0.2) !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'box-shadow 0.2s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: appDarkTheme.inputBorder,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(59, 192, 183, 0.2)',
          },
        },
        input: {
          color: appDarkTheme.textPrimary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: appDarkTheme.textMuted,
          '&.Mui-focused': {
            color: appDarkTheme.primary,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
          padding: 8,
          backgroundColor: appDarkTheme.backgroundPaper,
          color: appDarkTheme.textPrimary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.15)',
          backgroundColor: appDarkTheme.backgroundPaper,
          color: appDarkTheme.textPrimary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: `1px solid ${appDarkTheme.divider}`,
          backdropFilter: 'blur(20px) saturate(180%)',
          backgroundColor: 'rgba(24, 30, 36, 0.85)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          '& .MuiTableCell-head': {
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: appDarkTheme.textMuted,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
          borderBottomColor: appDarkTheme.divider,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${appDarkTheme.divider}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: appDarkTheme.divider,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${appDarkTheme.divider}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 12px 32px rgba(0,0,0,0.2)',
          },
        },
      },
    },
  },
});
